import { Box, Skeleton, TextField, Typography } from "@mui/material"
import type { ChangeEvent, FC, ReactElement } from "react"
import { useEffect, useRef, useState } from "react"
import { useShallow } from "zustand/shallow"

import { useCalculatedPricing, useSetPricingFetching } from "../../contexts/PurchaseOrderContext"
import { useCalculatePurchaseOrderPricing } from "../../hooks/useCalculatePurchaseOrderPricing"
import usePurchaseOrderForm from "../../hooks/usePurchaseOrderForm"

import { QuantityFieldUom } from "./QuantityFieldUom"

import { UOM_RENDER_PRECISION } from "@/constants/formats"
import { FIELD_LABEL_CLASSES } from "@/constants/forms"
import { type Product, type UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { parseInputQuantity } from "@/utils/conversion"
import { formatNumber, formatNumericInput } from "@/utils/number"
import { mapCalculatedPricingParams } from "@/utils/purchaseOrder"
import useDebounce from "@/utils/useDebounce"

interface QuantityFieldProps {
  quantityInUom: number
  uomId: string
  onInputChange: (args: { value: string; name: string }) => void
  uoms: UnitOfMeasurement[]
  product?: Product | null
  disabled?: boolean
}

export const QuantityField: FC<QuantityFieldProps> = ({
  quantityInUom,
  onInputChange,
  uoms,
  uomId,
  product,
  disabled = false,
}): ReactElement => {
  const [inputValue, setInputValue] = useState<string | number>(quantityInUom)
  const isUserInputRef = useRef(false)

  const setPricingFetching = useSetPricingFetching()

  const { getCalculatePurchaseOrderPricing } = useCalculatePurchaseOrderPricing()
  const getCalculatePurchaseOrderPricingDebounced = useDebounce(getCalculatePurchaseOrderPricing, 300)

  const { formValues: order } = usePurchaseOrderForm()

  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  useEffect(() => {
    setInputValue(parseInputQuantity(quantityInUom, isUserInputRef.current))
  }, [quantityInUom])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPricingFetching(true)
    const value = e.target.value
    isUserInputRef.current = true

    const formattedValue = formatNumericInput(value)
    setInputValue(formattedValue)
    onInputChange({ value: String(formattedValue), name: "quantityInUom" })

    const calculatedPricingParams = mapCalculatedPricingParams({
      orderForm: order,
      product: product!,
      quantity: +formattedValue,
    })

    getCalculatePurchaseOrderPricingDebounced(calculatedPricingParams)
  }

  const handleOnFocus = () => {
    if (inputValue === 0 || inputValue === "0") {
      setInputValue("")
    }
  }

  const handleOnBlur = () => {
    if (!inputValue && inputValue !== 0) {
      setInputValue(0)
      onInputChange({ value: "0", name: "quantityInUom" })
    }
    isUserInputRef.current = false
  }

  const { calculatedPricing, fetching: isPricingFetching } = useCalculatedPricing()

  const productCalculatedPricing = (calculatedPricing?.orderLines || []).find(
    ({ productId }) => productId === product?.productId
  )

  const oppositeQuantity = productCalculatedPricing?.opposite?.quantity ?? 0
  const oppositeQuantityUomId = productCalculatedPricing?.opposite?.quantityUomId

  const convertedUomName = units.find((u) => u.id === oppositeQuantityUomId)?.name ?? oppositeQuantityUomId

  const handleOnWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    ;(e.target as HTMLInputElement).blur()
  }

  return (
    <Box className='relative w-[220px]' data-testid='po-quantity-container'>
      <label className={FIELD_LABEL_CLASSES} htmlFor={`product-${product?.publicId}-quantity-input`}>
        Quantity
      </label>
      <TextField
        id={`product-${product?.publicId}-quantity-input`}
        fullWidth
        disabled={disabled}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onWheel={handleOnWheel}
        slotProps={{
          input: {
            type: "number",
            inputMode: "numeric",
            style: {
              MozAppearance: "textfield", // For Firefox
            },
            endAdornment: (
              <QuantityFieldUom onInputChange={onInputChange} filteredUoM={uoms} value={uomId} disabled={disabled} />
            ),
          },
        }}
        sx={{
          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
            WebkitAppearance: "none", // For Chrome, Safari, Edge
            margin: 0,
          },
        }}
      />
      {isPricingFetching ? (
        <Skeleton variant='text' sx={{ fontSize: "1rem" }} className='w-1/4' />
      ) : (
        <Typography variant='caption' className='pt-2 text-gray-700' data-testid='po-quantity-converted'>
          {formatNumber(oppositeQuantity, UOM_RENDER_PRECISION) || "-"} {convertedUomName}
        </Typography>
      )}
    </Box>
  )
}
